// Copyright (c) 2012-2021 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved. NOTICE: All information contained herein
// is, and remains the property of GRAB. The intellectual and technical concepts contained herein are confidential, proprietary
// and controlled by GRAB and may be covered by patents, patents in process, and are protected by trade secret or copyright law.
//
// You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate, adapt or change this material
// in any way unless prior written permission is obtained from GRAB. Access to the source code contained herein is hereby
// forbidden to anyone except current GRAB employees or contractors with binding Confidentiality and Non-disclosure agreements
// explicitly covering such access.
//
// The copyright notice above does not evidence any actual or intended publication or disclosure of this source code,
// which includes information that is confidential and/or proprietary, and is a trade secret, of GRAB.
//
// ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR THROUGH USE OF THIS SOURCE
// CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE LAWS AND
// INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY
// OR IMPLY ANY RIGHTS TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING
// THAT IT MAY DESCRIBE, IN WHOLE OR IN PART.

import React from 'react'
import {
  StarOutlined,
  CameraOutlined,
  CloudUploadOutlined,
  UserOutlined,
  BarChartOutlined,
  SettingOutlined,
  RightOutlined,
  ForkOutlined
} from '@ant-design/icons'
import { DetailCard } from 'components/detail-card'
import { Modal, Row, Col, Anchor, Space, Button, Typography, Tooltip } from 'antd'
import {
  OnlineStatusCell,
  CameraTypeCell,
  TaskTypeCell,
  TestModeCell,
  PowerSavingModeCell,
  IntervalTypeCell,
  UploadFormatCell,
  UploadQualityCell,
  UploadMethodCell,
  DataSaveModeCell,
  CaptureIntervalCell,
  RecordingHoursCell,
  AIModelCell,
  SettingSyncedCell,
  ImageCompressionCell,
  OnlineHoursCell
} from 'components/camera-details'
import { RootState, Dispatch } from 'store'
import { connect } from 'react-redux'
import HealthTag from 'components/HealthTag'
import { DeviceType, IDevice } from 'constants/device'
import { EnvironmentsOptions } from 'constants/device'
import { CombinedSettings, ILastSetting } from 'api/setting'
import { ComposedTitle } from 'components/composed-title'
import { DataUsageCell } from 'components/camera-directory/DataUsageCell'
import { BatteryLevelCell } from 'components/camera-directory/BatteryLevelCell'
import MaskLoading from 'components/MaskLoading'
import { GPSTrace } from 'components/icon'
import { SupportedAIModels } from '../../constants/AIModels'
import { formatDayInMonth } from '../../utils/time'
import moment from 'moment'

interface IOption {
  label: string,
  value: number | string
}

const getLabelByValue = (value: string|number, options:IOption[]) => {
 const target = options.find(item => item.value === value);
 return target ? target.label : 'unknown'
}

function mapState (state: RootState) {
  return {
    directoryState: state.directory,
    cityMap: state.city.cityMap
  }
}

function mapDispatch (dispatch: Dispatch) {
  return {
    directoryAction: dispatch.directory
  }
}

export type IDeviceDetailsProps = ReturnType<typeof mapState> &
  ReturnType<typeof mapDispatch> & {
    onViewTrace: (deviceID: string) => void
    onClose: () => void
    onClickEdit: (deviceID: string,groupID: string,cityID: string) => void
  }

class DeviceDetails extends React.Component<IDeviceDetailsProps> {
  private mainNode = React.createRef<HTMLDivElement>()

  private handleResetClick = deviceID => {
    const { directoryAction } = this.props
    Modal.confirm({
      title: 'Are you sure you want to reset the data usage?',
      onOk () {
        directoryAction.updateQuota(deviceID)
      }
    })
  }

  renderContent = () => {
    const { directoryState, cityMap } = this.props
    const { detail, detailLoading } = directoryState
    const device: IDevice = detail.device
    const setting: CombinedSettings = detail.setting
    const lastSetting: ILastSetting = detail.lastSetting

    if (detailLoading) {
      return <MaskLoading />
    }

    if (!device || !setting || !lastSetting) {
      return null
    }

    return (
      <>
        <div className='deviceID'>
          <Typography.Title level={3}>{device.deviceID}</Typography.Title>
        </div>
        <Anchor
          className='horizontal-anchor'
          affix={false}
          getContainer={() => this.mainNode.current || window}
          offsetTop={16}
        >
          <Anchor.Link href='#usageSnapshot' title={<StarOutlined />} />
          <Anchor.Link href='#camera' title={<CameraOutlined />} />
          <Anchor.Link href='#upload' title={<CloudUploadOutlined />} />
          <Anchor.Link href='#user' title={<UserOutlined />} />
          {/*{!!device.testMode &&*/}
          <Anchor.Link href='#aiModels' title={<ForkOutlined />} />
          {/*}*/}
          <Anchor.Link href='#dataQuota' title={<BarChartOutlined />} />
        </Anchor>
        <div className='main' ref={this.mainNode}>
          <DetailCard icon={<StarOutlined />} title='Usage Snapshot' id='usageSnapshot' className='detail-item'>
            <DetailCard.Row>
              <Row align='middle'>
                <Col span={12} className='title'>
                  Status
                  {/* <ComposedTitle primary='Status' secondary='' /> */}
                </Col>
                <Col span={12}>
                  <OnlineStatusCell onlineStatus={device.onlineStatus} />
                </Col>
              </Row>
            </DetailCard.Row>
            <DetailCard.Row>
              <Row align='middle'>
                <Col span={12} className='title'>
                  Health Track
                  {/* <ComposedTitle primary='Health Track' secondary='' /> */}
                </Col>
                <Col span={12}>
                  <HealthTag status={device.status} />
                </Col>
              </Row>
            </DetailCard.Row>
            <DetailCard.Row>
              <Row align='middle'>
                <Col span={12} className='title'>
                  {/* <ComposedTitle primary='Setting Synced' secondary='' /> */}
                  Setting Synced
                </Col>
                <SettingSyncedCell settingSynced={device.settingSynced} />
              </Row>
            </DetailCard.Row>
            <DetailCard.Row>
              <Row align='middle'>
                <Col span={12} className='title'>
                  {/* <Col span={12}> */}
                  Last Online
                  {/* <ComposedTitle primary='Last Online' secondary='' /> */}
                  {/* </Col> */}
                </Col>
                <Col span={12}>
                  {device.onlineTime === 0 ? '---' : moment.unix(device.onlineTime).format('MMM Do YYYY, h:mm A')}
                </Col>
              </Row>
            </DetailCard.Row>
            <DetailCard.Row>
              <Row align='middle'>
                <Col span={12} className='title'>
                  <ComposedTitle primary='# of Errors' secondary='Total past 7 days' />
                </Col>
                <Col span={12}>{device.errorsLast7Days}</Col>
              </Row>
            </DetailCard.Row>
            <DetailCard.Row>
              <Row align='middle'>
                <Col span={12} className='title'>
                  <ComposedTitle primary='Online Time' secondary='Avg past 7 days' />
                </Col>
                <Col span={12}>
                  <OnlineHoursCell onlineMinutes={device.onlinePeriodPerday} />
                </Col>
              </Row>
            </DetailCard.Row>
            <DetailCard.Row>
              <Row align='middle'>
                <Col span={12} className='title'>
                  <ComposedTitle primary='Data Usage' secondary='Total this month' />
                </Col>
                <Col span={8}>
                  <DataUsageCell
                    quotaUsage={device.quotaUsage}
                    quotaUsageLevel={device.quotaUsageLevel}
                    quotaUsagePercent={device.quotaUsagePercent}
                  />
                </Col>
                <Col span={4}>
                  <Button size={'small'} onClick={() => this.handleResetClick(device.deviceID)}>
                    Reset
                  </Button>
                </Col>
              </Row>
            </DetailCard.Row>
            <DetailCard.Row>
              <Row align='middle'>
                <Col span={12} className='title'>
                  <ComposedTitle primary='Unique KM' secondary='Avg past 7 days' />
                </Col>
                <Col span={12}>{device.uniqueDistPerday}</Col>
              </Row>
            </DetailCard.Row>
            <DetailCard.Row>
              <Row align='middle'>
                <Col span={12} className='title'>
                  Battery Level
                  {/* <ComposedTitle primary='Battery Level' secondary='' /> */}
                </Col>
                <Col span={12}>
                  <BatteryLevelCell battery={device.battery} />
                </Col>
              </Row>
            </DetailCard.Row>
            <DetailCard.Row>
              <Row align='middle'>
                <Col span={12} className='title'>
                  <ComposedTitle primary='Distance Travelled' secondary='Total all time' />
                </Col>
                <Col span={12}>{device.totalDistanceCovered} km</Col>
              </Row>
            </DetailCard.Row>
            <DetailCard.Row>
              <Row align='middle'>
                <Col span={12} className='title'>
                  <ComposedTitle primary='Online Time' secondary='Total all time' />
                </Col>
                <Col span={12}>
                  <OnlineHoursCell onlineMinutes={device.totalOnlinePeriod} />
                </Col>
              </Row>
            </DetailCard.Row>
          </DetailCard>
          <DetailCard icon={<CameraOutlined />} title='Camera' id='camera' className='detail-item'>
            <DetailCard.Row>
              <Row align='middle'>
                <Col span={12} className='title'>
                  CameraID
                  {/* <ComposedTitle primary='CameraID' secondary='' /> */}
                </Col>
                <Col span={12}>{device.deviceID}</Col>
              </Row>
            </DetailCard.Row>
            <DetailCard.Row>
              <Row align='middle'>
                <Col span={12} className='title'>
                  Camera Type
                  {/* <ComposedTitle primary='Camera Type' secondary='' /> */}
                </Col>
                <Col span={12}>
                  <CameraTypeCell type={device.deviceType} />
                </Col>
              </Row>
            </DetailCard.Row>
            <DetailCard.Row>
              <Row align='middle'>
                <Col span={12} className='title'>
                  Camera City
                </Col>
                <Col span={12}>{cityMap[device.cityID] ? cityMap[device.cityID].name : ''}</Col>
              </Row>
            </DetailCard.Row>
            <DetailCard.Row>
              <Row align='middle'>
                <Col span={12} className='title'>
                  Camera Task Type
                </Col>
                <Col span={12}>
                  <TaskTypeCell taskType={device.taskType} />
                </Col>
              </Row>
            </DetailCard.Row>
            <DetailCard.Row>
              <Row align='middle'>
                <Col span={12} className='title'>
                  Test mode
                </Col>
                <Col span={12}>
                  <TestModeCell testMode={device.testMode} />
                </Col>
              </Row>
            </DetailCard.Row>
            <DetailCard.Row>
              <Row align='middle'>
                <Col span={12} className='title'>
                  Power Saving Mode
                </Col>
                <Col span={12}>
                  {lastSetting.Id === 0 ? (
                    <span>{'---'}</span>
                  ) : (
                    <PowerSavingModeCell powerSave={lastSetting.PowerSave} />
                  )}
                </Col>
              </Row>
            </DetailCard.Row>
            <DetailCard.Row>
              <Row align='middle'>
                <Col span={12} className='title'>
                  Firmware Version
                </Col>
                <Col span={12}>{device.firmwareVersion === '' ? '---' : device.firmwareVersion}</Col>
              </Row>
            </DetailCard.Row>
            <DetailCard.Row>
              <Row align='middle'>
                <Col span={12} className='title'>
                  Camera Recording Hours
                </Col>
                <Col span={12}>
                  {lastSetting.Id === 0 ? (
                    <span>{'---'}</span>
                  ) : (
                    <RecordingHoursCell
                      workTimeStart={lastSetting.WorkTimeStart}
                      workTimeEnd={lastSetting.WorkTimeEnd}
                    />
                  )}
                </Col>
              </Row>
            </DetailCard.Row>
            <DetailCard.Row>
              <Row align='middle'>
                <Col span={12} className='title'>
                  CCS Mode
                </Col>
                <Col span={12}>
                  {lastSetting.Id === 0 ? (
                    <span>{'---'}</span>
                  ) : (
                    <IntervalTypeCell intervalType={lastSetting.IntervalType} />
                  )}
                </Col>
              </Row>
            </DetailCard.Row>
            <DetailCard.Row>
              <Row align='middle'>
                <Col span={12} className='title'>
                  CCS Interval
                </Col>
                <Col span={12}>
                  {lastSetting.Id === 0 ? (
                    <span>{'---'}</span>
                  ) : (
                    <CaptureIntervalCell
                      intervalType={lastSetting.IntervalType}
                      captureInterval={lastSetting.CaptureInterval}
                      captureDistanceInterval={lastSetting.CaptureDistanceInterval}
                    />
                  )}
                </Col>
              </Row>
            </DetailCard.Row>
            {/*
            <DetailCard.Row>
              <Row align='middle'>
                <Col span={12} className='title'>
                  Environment
                </Col>
                <Col span={12}>{getLabelByValue(device.environment, EnvironmentsOptions)}</Col>
              </Row>
            </DetailCard.Row>
            */}
          </DetailCard>
          <DetailCard icon={<CloudUploadOutlined />} title='Upload' id='upload' className='detail-item'>
            <DetailCard.Row>
              <Row align='middle'>
                <Col span={12} className='title'>
                  Upload Format
                </Col>
                <Col span={12}>
                  {lastSetting.Id === 0 ? (
                    <span>{'---'}</span>
                  ) : (
                    <UploadFormatCell uploadFormat={lastSetting.UploadFmt} />
                  )}
                </Col>
              </Row>
            </DetailCard.Row>
            <DetailCard.Row>
              <Row align='middle'>
                <Col span={12} className='title'>
                  Upload Quality
                </Col>
                <Col span={12}>
                  {lastSetting.Id === 0 ? (
                    <span>{'---'}</span>
                  ) : (
                    <UploadQualityCell
                      uploadFmt={lastSetting.UploadFmt}
                      imageryResolution={lastSetting.ImageryResolution}
                      videoResolution={lastSetting.VideoResolution}
                    />
                  )}
                </Col>
              </Row>
            </DetailCard.Row>
            <DetailCard.Row>
              <Row align='middle'>
                <Col span={12} className='title'>
                  Image Quality
                </Col>
                <Col span={12}>
                  {lastSetting.Id === 0 ? (
                    <span>{'---'}</span>
                  ) : (
                    <ImageCompressionCell imageCompressRatio={lastSetting.ImageCompressRatio} />
                  )}
                </Col>
              </Row>
            </DetailCard.Row>
            <DetailCard.Row>
              <Row align='middle'>
                <Col span={12} className='title'>
                  Data Saver Mode
                </Col>
                <Col span={12}>
                  {lastSetting.Id === 0 ? (
                    <span>{'---'}</span>
                  ) : (
                    <DataSaveModeCell imageryFormat={lastSetting.ImageryFmt} />
                  )}
                </Col>
              </Row>
            </DetailCard.Row>
            <DetailCard.Row>
              <Row align='middle'>
                <Col span={12} className='title'>
                  Upload Method
                </Col>
                <Col span={12}>
                  {lastSetting.Id === 0 ? (
                    <span>{'---'}</span>
                  ) : (
                    <UploadMethodCell uploadMethod={lastSetting.UploadMethod} />
                  )}
                </Col>
              </Row>
            </DetailCard.Row>
          </DetailCard>
          <DetailCard icon={<UserOutlined />} title='User' id='user' className='detail-item'>
            <DetailCard.Row>
              <Row align='middle'>
                <Col span={12} className='title'>
                  Driver ID
                </Col>
                <Col span={12}>{device.driverID ? device.driverID : '---'}</Col>
              </Row>
            </DetailCard.Row>
          </DetailCard>
          {/*{!!device.testMode && (*/}
            <DetailCard icon={<ForkOutlined />} title='AI Models' id='aiModels' className='detail-item'>
              {/*{SupportedAIModels.map(({ key, name }) => (
                <DetailCard.Row key={key}>
                  <Row align='middle'>
                    <Col span={12}>{name}</Col>
                    <Col span={12}>
                      <AIModelCell enabled={lastSetting.AiImageQuality} />
                    </Col>
                  </Row>
                </DetailCard.Row>
              ))}*/}
              {/*temporarily keep only aiImageQuality*/}
              <DetailCard.Row>
                <Row align='middle'>
                  <Col span={12} className='title'>
                    {'Image Quality'}
                  </Col>
                  <Col span={12}>
                    {lastSetting.Id === 0 ? <span>{'---'}</span> : <AIModelCell enabled={lastSetting.AiImageQuality} />}
                  </Col>
                </Row>
              </DetailCard.Row>
              <DetailCard.Row>
                <Row align='middle'>
                  <Col span={12} className='title'>
                    {'Privacy'}
                  </Col>
                  <Col span={12}>
                    {lastSetting.Id === 0 ? <span>{'---'}</span> : <AIModelCell enabled={lastSetting.AiPrivacy} />}
                  </Col>
                </Row>
              </DetailCard.Row>
            </DetailCard>
          {/*)}*/}
          <DetailCard icon={<BarChartOutlined />} title='DataQuota' id='dataQuota' className='detail-item'>
            <DetailCard.Row>
              <Row align='middle'>
                <Col span={12} className='title'>
                  Monthly Data Quota
                </Col>
                <Col span={12}>{setting.trafficQuota}GB</Col>
              </Row>
            </DetailCard.Row>
            <DetailCard.Row>
              <Row align='middle'>
                <Col span={12} className='title'>
                  Data Management Cycle
                </Col>
                <Col span={12}>
                  {`${device.billingCycleStart}${formatDayInMonth(device.billingCycleStart)} of the month`}
                </Col>
              </Row>
            </DetailCard.Row>
            <DetailCard.Row>
              <Row align='middle'>
                <Col span={12} className='title'>
                  Data Alert Limit
                </Col>
                <Col span={12}>{setting.alertThreshold}%</Col>
              </Row>
            </DetailCard.Row>
            <DetailCard.Row>
              <Row align='middle'>
                <Col span={12} className='title'>
                  Data Hard Limit
                </Col>
                <Col span={12}>{setting.hardThreshold}%</Col>
              </Row>
            </DetailCard.Row>
          </DetailCard>
          <div style={{ height: 800 }}> </div>
        </div>
      </>
    )
  }
  render () {
    const { detail}  = this.props.directoryState
    const { onClose, onClickEdit, onViewTrace } = this.props
    const {device } =detail || {}
    const {deviceID,groupID,cityID, deviceType} =device || {}
    const disableChangeSetting = deviceType === undefined || deviceType === DeviceType.Lite360Slave

    return (
      <div className='device-details'>
        <Row justify='end' align='middle' className='header'>
          <Col>
            <Space>
              <Tooltip title='Change Settings'>
                <Button
                  type='text'
                  onClick={() => onClickEdit(deviceID,groupID,cityID)}
                  disabled={disableChangeSetting}
                >
                  <SettingOutlined />
                </Button>
              </Tooltip>
              <Tooltip title='GPS trace'>
                <Button disabled={!detail.device} onClick={() => onViewTrace(detail.device.deviceID)} type='text'>
                  <GPSTrace />
                </Button>
              </Tooltip>
              <Tooltip title='close'>
                <Button type='text' onClick={onClose}>
                  <RightOutlined />
                </Button>
              </Tooltip>
            </Space>
          </Col>
        </Row>
        {this.renderContent()}
      </div>
    )
  }
}

export default connect(mapState, mapDispatch)(DeviceDetails)
